import { DestinationItem, PackageParams } from '@ibe/components';
import { encodeObjectasUrlParam, LoggerFactory, removeUndefinedProps } from '@ibe/services';
import { ApiGeoUnit, ApiGeoUnitFromJSON } from '@ibe/api';

export interface RawPackageParams {
  start?: string;
  end?: string;
  origin?: string;
  origins?: string;
  originCode?: string;
  destination?: string;
  destinations?: string;
  destinationCode?: string;
  detail?: string;
  item?: string;
  date?: string;
  occupancy?: string;
  adults?: string;
  children?: string;
  infants?: string;
  childrenAges?: string;
  listOptions?: string;
  packageServiceGroup?: string;
  productId?: string;
  goToDetails?: string;
  destinationItem?: string;
  mainServiceCodes?: string;
  preSelectedCharacteristics?: string;
  inventories?: string;
  duration?: string;
  isNewTab?: boolean;
  packageCode?: string;
}

const logger = LoggerFactory.get('PackageParamsTransformer');

class PackageParamsTransformer {
  static decode(
    urlParams: RawPackageParams & { isNewTab?: boolean }
  ): Partial<PackageParams & { isNewTab?: boolean }> {
    const params: Partial<PackageParams & { isNewTab?: boolean }> = {};
    if (!urlParams) {
      return params;
    }
    try {
      params.startDate = urlParams.start || undefined;
      params.endDate = urlParams.end || undefined;
      params.packageDetailId = urlParams.detail || undefined;
      params.travelDateId = urlParams.date || undefined;
      params.itemId = urlParams.item || undefined;
      params.productId = urlParams.productId || undefined;
      params.packageCode = urlParams.packageCode || undefined;
      if (urlParams.goToDetails) {
        params.goToDetails = JSON.parse(urlParams.goToDetails);
      }
      if (urlParams.destinationItem) {
        params.destinationItem = JSON.parse(urlParams.destinationItem);
      }
      if (urlParams.mainServiceCodes) {
        params.mainServiceCodes = JSON.parse(urlParams.mainServiceCodes);
      }
      if (urlParams.preSelectedCharacteristics) {
        params.preSelectedCharacteristics = JSON.parse(urlParams.preSelectedCharacteristics);
      }
      if (urlParams.inventories) {
        params.inventories = JSON.parse(urlParams.inventories);
      }
      if (urlParams.duration) {
        params.duration = JSON.parse(urlParams.duration);
      }
      if (urlParams.occupancy) {
        params.occupancy = JSON.parse(urlParams.occupancy);
      } else if (urlParams.adults || urlParams.children || urlParams.infants) {
        params.occupancy = [
          {
            adults: urlParams.adults ? Number(urlParams.adults) : 2,
            children: urlParams.children ? Number(urlParams.children) : 0,
            infants: urlParams.infants ? Number(urlParams.infants) : 0,
            childrenAges: urlParams.childrenAges ? JSON.parse(urlParams.childrenAges) : [],
            infantsAges: []
          }
        ];
      }
      if (urlParams.origin) {
        params.origin = JSON.parse(urlParams.origin);
      } else if (urlParams.originCode) {
        params.origin = ApiGeoUnitFromJSON({
          id: '',
          code: urlParams.originCode,
          name: '',
          typeCode: '',
          children: {}
        });
      }
      if (urlParams.origins) {
        params.origins = this.decodeOrigins(urlParams.origins);
      }
      if (urlParams.destination) {
        params.destination = JSON.parse(urlParams.destination);
      }
      if (urlParams.destinations) {
        params.destinations = this.decodeDestinations(urlParams.destinations);
      }
      params.packageServiceGroup = urlParams.packageServiceGroup || undefined;
      if (urlParams.listOptions) {
        params.listOptions = JSON.parse(urlParams.listOptions);
      }
      params.isNewTab = urlParams.isNewTab;
    } catch (e) {
      logger.error(e);
    }
    return removeUndefinedProps(params);
  }

  static encode(params: Partial<PackageParams>): string {
    if (params.destinationItem) {
      params.destinationItem.name = encodeURIComponent(params.destinationItem.name);
    }
    const obj: RawPackageParams = {
      start: params.startDate,
      end: params.endDate,
      origin: JSON.stringify(params.origin),
      listOptions: JSON.stringify(params.listOptions),
      origins: this.encodeOrigins(params.origins),
      destinations: this.encodeDestinations(params.destinations),
      productId: params.productId,
      occupancy: JSON.stringify(params.occupancy),
      date: params.travelDateId,
      detail: params.packageDetailId,
      item: params.itemId,
      packageServiceGroup: params.packageServiceGroup,
      goToDetails: JSON.stringify(params.goToDetails),
      destinationItem: JSON.stringify(params.destinationItem),
      mainServiceCodes: JSON.stringify(params.mainServiceCodes),
      packageCode: params?.packageCode,
      preSelectedCharacteristics: JSON.stringify(params.preSelectedCharacteristics),
      inventories: JSON.stringify(params.inventories),
      duration: JSON.stringify(params.duration)
    };

    return encodeObjectasUrlParam(removeUndefinedProps(obj));
  }

  private static decodeDestinations(destinations: string): DestinationItem[] {
    const destinationsCodes: string[] = JSON.parse(destinations);
    return destinationsCodes.map(code => ({ code }) as DestinationItem);
  }

  private static encodeDestinations(destinations: DestinationItem[] | undefined): string {
    return destinations ? JSON.stringify(destinations.map(dst => dst.code)) : '[]';
  }

  private static decodeOrigins(origins: string): ApiGeoUnit[] {
    const originsCodes: string[] = JSON.parse(origins);
    return originsCodes.map(code => ({ code }) as ApiGeoUnit);
  }

  private static encodeOrigins(origins: ApiGeoUnit[] | undefined): string {
    return origins ? JSON.stringify(origins.map(origin => origin.code)) : '[]';
  }
}

export default PackageParamsTransformer;
